import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Design System
import AddressWithSearchForm from 'jl-design-system/components/address-with-search-form/AddressWithSearchForm';
import { Message } from 'jl-design-system/components/message/Message';
import { SecondaryButton, PrimaryButton, UnderlinedButton } from 'jl-design-system/elements/button/Button';
import Link from 'jl-design-system/elements/link/Link';
import replaceStringVars from 'jl-design-system/utils/string/replaceStringVars';

// Types
import { AddressRecordProps } from 'types/Address.types';
import { AppDispatch } from 'types/RootState.types';
import { DeliveryAddressProps } from './DeliveryAddress.types';

// Config
import deliveryAddressState from './DeliveryAddress.state';
import { DELIVERY_ADDRESS } from '@constants/actionConstants';
import deliveryConstants from '@constants/deliveryConstants';
import routeConstants from '@constants/routeConstants';
import getLoqateMocks from '@non-prod-utils/utils/loqate/getLoqateMocks';
import {
  onLoqateSearch,
  triggerLoqateTimeoutEnterAddressManuallyAnalytics,
  onLoqateNetworkTimeoutCallback,
  onEnterManualAddress,
} from '@redux/actions/address/addressActions';
import { createAddress, updateAddress } from '@redux/actions/addressBook/addressBookActions';
import { setScrollToDeliveryDetails } from '@redux/actions/app/appActions';
import {
  editDeliveryAddress,
  setDeliveryAddressAndGetDeliveryMethods,
  resetDeliveryAddressAndMethods,
  changeDeliveryAddress,
  onUseAddressButtonClick,
} from '@redux/actions/delivery/deliveryActions';
import { showEditBasketModal } from '@redux/actions/edit-basket/editBasketActions';
import { saveForLaterAvailableForUndeliverableServiceParent } from '@redux/reducers/delivery/deliveryContainsUndeliverableServices';
import { shouldMockLoqate } from '@utils/address/addressHelpers';
import errorMessageBodyLookup from '@utils/error/errorMessageBodyLookup';
import { getDeliveryAddressFormConfig, DELIVERY_ADDRESS_FORM_ID } from '@utils/form/configs/deliveryAddress';
import { itemsContainM2M } from '@utils/orderform/isItemFabric';

// Components
import AddressBookModal from '@components/address-book-modal';
import BodyText from '@components/body-text';
import Container from '@components/container';
import DeliveryAddressCard from '@components/delivery-address-card/DeliveryAddressCard';
import AddressBookCarousel from '@components/delivery-address-carousel';
import DisplayAddress from '@components/display-address/DisplayAddress';
import Heading from '@components/heading';
import InvalidAddressForm from '@components/invalid-address-form/InvalidAddressForm';
import UndeliverableErrorMessage, {
  getUndeliverableErrorMessage,
} from '@components/undeliverable-error-message/UndeliverableErrorMessage';
import UndeliverableServicesError from '@components/undeliverable-services-error';
import UserEmailAddress from '@components/user-email-address';

// Styles
import styles from './delivery-address.scss';

const DeliveryAddress = ({ headingTag = 'h3', disabled }: DeliveryAddressProps) => {
  const {
    addressBook = [],
    addressSearchComplete,
    canUseSelectedDeliveryAddress,
    confirmedDeliveryAddress,
    countryCode = '',
    deliveries = [],
    deliveryContainsIncompatibleServices,
    deliveryContainsUndeliverableServices,
    incompleteDeliveryDetailsError,
    isChangingAddress,
    isCurrentlyDisabled,
    isExpressCheckout,
    isLoadingLazyComponent,
    isNewAddressLayout,
    isSignedIn,
    isSignedInWithData,
    items,
    loqateApiKey,
    pathname,
    postcodeOutOfAreaError,
    putDeliveryAddressError,
    saveAddressError,
    selectedDeliveryAddress,
    servicesThatNeedRemoving,
    sessionId,
    shouldReFetchDeliveryOptions,
    showAddressBookModal,
    undeliverableItemsInBasket = [],
    userEmailAddress,
    useThisAddressButtonSubmitting,
  } = useSelector(deliveryAddressState);

  const dispatch: AppDispatch = useDispatch();

  const saveForLaterAvailableOnUndeliverableServiceParent = saveForLaterAvailableForUndeliverableServiceParent({
    servicesThatNeedRemoving,
    deliveries: deliveries,
    isSignedIn,
    isExpressCheckout: isExpressCheckout,
    isM2M: itemsContainM2M(items),
    isShipFromStore: items?.some((item) => item?.availability?.shipFromStore),
  });

  const isDisabled = disabled || isCurrentlyDisabled;

  const [isChoosingAddress, setIsChoosingAddress] = useState(false);

  useEffect(() => {
    if (addressSearchComplete) {
      return;
    }

    const shouldSetDeliveryAddressAndGetDeliveryMethods = selectedDeliveryAddress && shouldReFetchDeliveryOptions;

    if (shouldSetDeliveryAddressAndGetDeliveryMethods) {
      dispatch(setScrollToDeliveryDetails(true));
      dispatch(
        setDeliveryAddressAndGetDeliveryMethods({
          formValues: undefined,
          address: selectedDeliveryAddress,
        }),
      );
    }
  }, [
    addressSearchComplete,
    shouldReFetchDeliveryOptions,
    selectedDeliveryAddress,
    setScrollToDeliveryDetails,
    setDeliveryAddressAndGetDeliveryMethods,
  ]);

  const addressId = DELIVERY_ADDRESS;

  const onChangeAddressClick = async () => {
    setIsChoosingAddress(true);

    if (isNewAddressLayout) {
      return;
    }
    dispatch(changeDeliveryAddress());
  };

  const onEditAddressClick = () => {
    dispatch(editDeliveryAddress(selectedDeliveryAddress));
  };

  const onAddressFormSubmit = (formValues: AddressRecordProps) => dispatch(onUseAddressButtonClick(formValues));

  const onSearchForAnotherAddress = () => {
    dispatch(resetDeliveryAddressAndMethods({ addressId, countryCode }));
  };

  const isMultiBatch = deliveries.length > 1;

  const getUndeliverableServices = () => {
    const undeliverableServices = deliveries.reduce((acc: any[], delivery) => {
      if (delivery.undeliverableServices) {
        return [...acc, ...(delivery.undeliverableServices.items ?? [])];
      }
      return acc;
    }, []);

    return undeliverableServices;
  };

  const renderEmail = (margin?: boolean) => <UserEmailAddress isMarginApplied={margin} />;

  const editBasket = () => {
    showEditBasketModal({ baseRoutePath: pathname });
  };

  const configExtras = {
    styles,
  };

  const footerText = deliveryContainsIncompatibleServices ? (
    <>
      <p>
        Please <Link to={routeConstants.EDIT_BASKET}>edit your basket</Link> and remove a service.
        {saveForLaterAvailableOnUndeliverableServiceParent && (
          <> Or you can select ‘save for later’ to continue in 2 separate orders.</>
        )}
        {!saveForLaterAvailableOnUndeliverableServiceParent && (
          <> Or you can remove an item to continue in 2 separate orders.</>
        )}
      </p>
    </>
  ) : (
    <p>
      To continue, please{' '}
      <UnderlinedButton onClick={onChangeAddressClick}>choose a different delivery address</UnderlinedButton>, or{' '}
      <Link to={routeConstants.EDIT_BASKET}>edit your basket</Link> to remove the service.
    </p>
  );

  const deliveryRemovedServiceItems =
    servicesThatNeedRemoving && servicesThatNeedRemoving.length ? (
      <>
        <ul className={styles.removedServicesList}>
          {servicesThatNeedRemoving.map((item, index) => {
            const key = `rangeTimes-${index}`;
            return <li key={`removedService-${key}`}>{`${item.name} - For ${item.name}`}</li>;
          })}
        </ul>

        {footerText}
      </>
    ) : undefined;

  const invalidAddressMessageBody = isSignedInWithData
    ? 'Some of the information in your default delivery address is missing or invalid. Please update this information or change the address to continue.'
    : '';

  if (incompleteDeliveryDetailsError && confirmedDeliveryAddress) {
    incompleteDeliveryDetailsError.body = 'Please select the ‘Continue to payment’ button to proceed.';
  }

  const undeliverableItemsMessage = getUndeliverableErrorMessage({
    items,
    editBasket: editBasket,
    onChangeAddressClick: onChangeAddressClick,
    undeliverableItemsInBasket,
  });
  const undeliverableServices = getUndeliverableServices();
  const deliveryHeading = isNewAddressLayout
    ? isChoosingAddress
      ? 'Choose delivery address'
      : 'Your preferred delivery details'
    : 'Delivery details';

  const saveSelectedDeliveryAddressAsDefaultProps = canUseSelectedDeliveryAddress
    ? { saveAsDefaultObject: selectedDeliveryAddress, saveAsDefaultType: deliveryConstants.DELIVERY }
    : undefined;

  return (
    <Container background="white" className={styles.container} marginBottom="0" padding="3">
      {!isMultiBatch && deliveryContainsUndeliverableServices && (
        <UndeliverableServicesError items={undeliverableServices} />
      )}

      <Heading label={deliveryHeading} marginBottom="3" tag={headingTag} testId="delivery-title" type="s" />
      <>
        <Container marginBottom="2" renderIf={!!postcodeOutOfAreaError}>
          <Message
            body={errorMessageBodyLookup({
              body: postcodeOutOfAreaError?.body,
              bodyLookup: postcodeOutOfAreaError?.code,
            })}
            dataTestId="delivery-address-postcode-out-of-area-error"
            title={replaceStringVars(postcodeOutOfAreaError?.message, {
              postcode: selectedDeliveryAddress?.address?.postcode ?? '',
            })}
            type="error"
          />
        </Container>
        <Container marginBottom="2" renderIf={!!incompleteDeliveryDetailsError}>
          <Message
            body={incompleteDeliveryDetailsError?.body}
            dataTestId="delivery-address-incomplete-details-error"
            title={incompleteDeliveryDetailsError?.message}
            type="error"
          />
        </Container>
        <Container
          marginBottom="2"
          renderIf={Array.isArray(servicesThatNeedRemoving) && servicesThatNeedRemoving.length > 0}
        >
          <Message
            body={deliveryRemovedServiceItems || 'Your basket has been updated to reflect this.'}
            dataId="delivery-removed-service-message"
            dataTestId="delivery-removed-service-message"
            scrollToMessageOnMount
            title={
              deliveryContainsIncompatibleServices
                ? 'Sorry, the following services cannot be purchased together'
                : 'Sorry, one or more services in your order are unavailable'
            }
            type="error"
          />
        </Container>
        {undeliverableItemsMessage?.length &&
          !isChangingAddress &&
          undeliverableItemsMessage.map((message, ind) => (
            <UndeliverableErrorMessage key={`message-${ind + 1}`} undeliverableItemsMessage={message} />
          ))}
        <Container marginBottom="2" renderIf={!!putDeliveryAddressError && !postcodeOutOfAreaError}>
          <Message
            body={errorMessageBodyLookup({
              bodyLookup: putDeliveryAddressError?.bodyLookup,
              body: putDeliveryAddressError?.body,
            })}
            title={putDeliveryAddressError?.message}
            type="error"
          />
        </Container>
      </>
      {!selectedDeliveryAddress && !isNewAddressLayout && renderEmail(true)}
      <Container renderIf={isNewAddressLayout}>
        <Heading type="xs">Account Email</Heading>
        <BodyText marginBottom="1">{userEmailAddress}</BodyText>
        {isChoosingAddress && (
          <AddressBookCarousel
            addressBook={addressBook}
            configExtras={configExtras}
            formId={DELIVERY_ADDRESS_FORM_ID}
            getConfig={getDeliveryAddressFormConfig}
            toggleAddNewAddressForm={setIsChoosingAddress}
          />
        )}
      </Container>

      {!selectedDeliveryAddress && !confirmedDeliveryAddress && (
        <>
          <AddressWithSearchForm
            addressId={addressId}
            combineExtraAddressLines
            configExtras={configExtras}
            data-testid="delivery-address-form"
            destroyOnUnmount={false}
            formId={DELIVERY_ADDRESS_FORM_ID}
            getConfig={getDeliveryAddressFormConfig}
            getLoqateMocks={getLoqateMocks}
            jlpid={sessionId}
            loqateApiKey={loqateApiKey}
            maxAddressLines={3}
            multiFormValidation
            onAddressFormSubmit={onAddressFormSubmit}
            onEnterManualAddress={onEnterManualAddress}
            onLoqateNetworkTimeoutCallback={onLoqateNetworkTimeoutCallback}
            onLoqateSearch={onLoqateSearch}
            onLoqateTimeoutEnterAddressManuallyCallback={triggerLoqateTimeoutEnterAddressManuallyAnalytics}
            onSearchForAnotherAddressOverride={onSearchForAnotherAddress}
            replaceGbTerritoryCountryCodes={false}
            shouldMockLoqate={shouldMockLoqate()}
            useLoqateAddressSearch
          />
        </>
      )}

      {selectedDeliveryAddress && !confirmedDeliveryAddress && (
        <>
          {selectedDeliveryAddress.invalid && !showAddressBookModal && (
            <InvalidAddressForm
              addressBook={addressBook}
              addressFormId={DELIVERY_ADDRESS_FORM_ID}
              configExtras={configExtras}
              getConfig={getDeliveryAddressFormConfig}
              maxAddressLines={3}
              messageBody={invalidAddressMessageBody}
              onChangeAddress={onChangeAddressClick}
              onCreateAddress={formValues => dispatch(createAddress(formValues))}
              onUpdateAddress={formValues => dispatch(updateAddress(formValues))}
              savedAddress={selectedDeliveryAddress}
              showChangeAddressButton={isSignedInWithData}
            />
          )}

          {!selectedDeliveryAddress.invalid && (
            <>
              {isNewAddressLayout ? (
                <DeliveryAddressCard
                  address={selectedDeliveryAddress.address}
                  addressId={selectedDeliveryAddress.id}
                  addressee={selectedDeliveryAddress.addressee}
                  handleChangeAddressClick={onEditAddressClick}
                  phoneNumber={selectedDeliveryAddress.phoneNumber}
                />
              ) : (
                <>
                  <DisplayAddress
                    address={selectedDeliveryAddress.address}
                    addressee={selectedDeliveryAddress.addressee}
                    data-testid="delivery-address-selected"
                    phoneNumber={selectedDeliveryAddress.phoneNumber}
                    saveAddressError={!!saveAddressError}
                    {...saveSelectedDeliveryAddressAsDefaultProps}
                  />

                  {!canUseSelectedDeliveryAddress && !isNewAddressLayout && (
                    <div className={styles.buttonsContainer}>
                      <PrimaryButton
                        className={styles.changeAddressButton}
                        data-testid="delivery-address-change-button"
                        disabled={isDisabled}
                        onClick={onChangeAddressClick}
                        submitting={isLoadingLazyComponent}
                      >
                        Change address
                      </PrimaryButton>
                    </div>
                  )}
                </>
              )}
              {canUseSelectedDeliveryAddress && (
                <>
                  {
                    // address editing for customer with address book takes place in address book modal
                    !isSignedInWithData && (
                      <UnderlinedButton
                        className={styles.editAddressButton}
                        data-testid="delivery-address-edit-address"
                        disabled={isDisabled}
                        onClick={onEditAddressClick}
                      >
                        Edit address
                      </UnderlinedButton>
                    )
                  }

                  {renderEmail()}

                  <div className={styles.buttonsContainer}>
                    <PrimaryButton
                      className={styles.useAddressButton}
                      data-testid="delivery-address-use-address"
                      disabled={isDisabled}
                      onClick={() => dispatch(onUseAddressButtonClick())}
                      submitting={useThisAddressButtonSubmitting}
                    >
                      Use this address
                    </PrimaryButton>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}

      {confirmedDeliveryAddress && (
        <>
          {saveAddressError && (
            <Container marginBottom="2">
              <Message
                body={saveAddressError.body}
                dataTestId="save-address-failed-message"
                title={saveAddressError.message}
                type="information"
              />
            </Container>
          )}

          {isNewAddressLayout ? (
            <Container>
              {!isChoosingAddress && (
                <DeliveryAddressCard
                  address={confirmedDeliveryAddress.address}
                  addressId={confirmedDeliveryAddress.id}
                  addressee={confirmedDeliveryAddress.addressee}
                  handleChangeAddressClick={onChangeAddressClick}
                  phoneNumber={confirmedDeliveryAddress.phoneNumber}
                />
              )}
            </Container>
          ) : (
            <div className={styles.deliveryAddressWrapper}>
              <DisplayAddress
                address={confirmedDeliveryAddress.address}
                addressee={confirmedDeliveryAddress.addressee}
                data-testid="delivery-address-confirmed"
                isUndeliverableAddress={!!undeliverableItemsMessage?.length}
                phoneNumber={confirmedDeliveryAddress.phoneNumber}
                saveAddressError={!!saveAddressError}
                saveAsDefaultObject={confirmedDeliveryAddress}
                saveAsDefaultType={deliveryConstants.DELIVERY}
              />
            </div>
          )}

          {!isNewAddressLayout && renderEmail()}

          {!isNewAddressLayout && undeliverableItemsInBasket?.length === 0 && (
            <SecondaryButton
              className={styles.changeAddressButton}
              data-testid="delivery-address-change-button"
              disabled={isDisabled}
              onClick={onChangeAddressClick}
              submitting={isLoadingLazyComponent}
            >
              Change address
            </SecondaryButton>
          )}

          {!isNewAddressLayout && (undeliverableItemsInBasket?.length ?? 0) > 0 && (
            <div className={styles.undeliverableItemsActions}>
              <PrimaryButton
                data-testid="delivery-address-change-button-primary"
                onClick={onChangeAddressClick}
                submitting={isLoadingLazyComponent}
              >
                Change address
              </PrimaryButton>
              {undeliverableItemsInBasket?.length !== items.length && (
                <SecondaryButton onClick={editBasket}>Edit basket</SecondaryButton>
              )}
            </div>
          )}
        </>
      )}

      {showAddressBookModal && (
        <AddressBookModal
          baseRoutePath={routeConstants.DELIVERY}
          configExtras={configExtras}
          formId={DELIVERY_ADDRESS_FORM_ID}
          getConfig={getDeliveryAddressFormConfig}
        />
      )}
    </Container>
  );
};

export default DeliveryAddress;
